import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  AuthService,
  readStoredBusinessId,
  readStoredChatbotNumber,
  readStoredOrgId,
  readStoredOrgName,
  readStoredPhoneNumberId,
  readStoredWabaId,
} from '../../shared/auth.service';
import { InvoiceApiItem, InvoicesApiService } from '../../shared/invoices-api.service';

@Component({
  standalone: true,
  selector: 'app-profile-page',
  imports: [CommonModule],
  template: `
  <div class="page">
    <div class="hero">
      <div class="ic">👤</div>
      <div>
        <div class="h1">Settings</div>
        <div class="h2">Configuration and account security</div>
      </div>
    </div>

    <div class="layout">
      <aside class="menu card">
        <button class="menuItem" [class.active]="activeTab()==='config'" (click)="activeTab.set('config')">Config</button>
        <button class="menuItem" [class.active]="activeTab()==='invoice'" (click)="activeTab.set('invoice')">Invoice</button>
      </aside>

      <section class="panel card" *ngIf="activeTab()==='config'">
        <div class="t">Workspace Config</div>
        <div class="m">Values are fetched from your login session and are read-only.</div>

        <div class="metaGrid">
          <div class="metaLabel">Organization ID</div>
          <div class="metaValue">{{ orgId || '-' }}</div>

          <div class="metaLabel">Organization Name</div>
          <div class="metaValue">{{ orgName || '-' }}</div>

          <div class="metaLabel">Chatbot Number</div>
          <div class="metaValue">{{ chatbotNumber || '-' }}</div>

          <div class="metaLabel">WABA ID</div>
          <div class="metaValue">{{ wabaId || '-' }}</div>

          <div class="metaLabel">Phone Number ID</div>
          <div class="metaValue">{{ phoneNumberId || '-' }}</div>

          <div class="metaLabel">Business ID</div>
          <div class="metaValue">{{ businessId || '-' }}</div>
        </div>
      </section>

      <section class="panel card" *ngIf="activeTab()==='invoice'">
        <div class="t">Invoices</div>
        <div class="m">Billing history with downloadable invoice records.</div>

        <div class="invoiceTableWrap">
          <div class="invoiceHead">
            <div>Invoice ID</div>
            <div>Created At</div>
            <div>Invoice URL</div>
            <div>Download</div>
          </div>
          <div class="invoiceRow" *ngFor="let inv of pagedInvoices()">
            <div class="invId">{{ inv.id }}</div>
            <div>{{ inv.createdAt }}</div>
            <div class="invUrl">
              <a [href]="inv.url" target="_blank" rel="noopener">{{ inv.url }}</a>
            </div>
            <div>
              <a class="pagerBtn invoiceBtn" [href]="inv.url" download>Download</a>
            </div>
          </div>
          <div class="m emptyRow" *ngIf="invoiceLoading">Loading invoices...</div>
          <div class="m emptyRow" *ngIf="!invoiceLoading && invoiceError">{{ invoiceError }}</div>
          <div class="m emptyRow" *ngIf="!invoiceLoading && !invoiceError && !invoices.length">No invoices found.</div>
        </div>

        <div class="pager" *ngIf="invoices.length">
          <button class="pagerBtn" (click)="goToPrevInvoicePage()" [disabled]="invoicePage() <= 1">Previous</button>
          <div class="m pageInfo">Page {{ invoicePage() }} / {{ totalInvoicePages() }}</div>
          <button class="pagerBtn" (click)="goToNextInvoicePage()" [disabled]="invoicePage() >= totalInvoicePages()">Next</button>
        </div>
      </section>

    </div>

    <div class="toast" *ngIf="toast()">{{ toast() }}</div>

    <div class="accountActions card">
      <div class="t">Session</div>
      <div class="m">Securely sign out from this workspace.</div>
      <button class="btn dangerBtn" (click)="logout()">Logout</button>
    </div>
  </div>
  `,
  styles: [`
    .page{flex:1; padding:16px; overflow:auto}
    .hero{display:flex; gap:12px; align-items:center; padding:14px; border-radius:18px;
      border:1px solid var(--border); background:rgba(17,27,33,.55)}
    .ic{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;
      background:rgba(0,168,132,.16);border:1px solid rgba(0,168,132,.35);font-size:20px}
    .h1{font-size:18px;font-weight:900}
    .h2{margin-top:2px;color:var(--muted);font-size:12px}
    .layout{margin-top:12px;display:grid;grid-template-columns:260px 1fr;gap:12px}
    .card{padding:14px;border-radius:18px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .menu{display:flex;flex-direction:column;gap:8px}
    .menuItem{
      text-align:left;padding:10px 12px;border-radius:12px;border:1px solid transparent;background:transparent;color:var(--muted);cursor:pointer
    }
    .menuItem:hover{background:rgba(42,57,66,.35);color:var(--text)}
    .menuItem.active{background:rgba(0,168,132,.15);border-color:rgba(0,168,132,.35);color:var(--text)}
    .panel{min-height:420px}
    .t{font-weight:900}
    .m{margin-top:6px;color:var(--muted);font-size:12px;line-height:1.35}
    .form{margin-top:12px;display:grid;gap:8px;max-width:520px}
    .form label{font-size:12px;color:var(--muted)}
    .form input{
      height:38px;border-radius:10px;border:1px solid var(--border);background:var(--input);color:var(--text);padding:0 10px;outline:none
    }
    .metaGrid{margin-top:12px;display:grid;grid-template-columns:220px 1fr;gap:8px 12px;align-items:center}
    .metaLabel{font-size:12px;color:var(--muted)}
    .metaValue{
      min-height:38px;display:flex;align-items:center;padding:0 10px;border-radius:10px;
      border:1px solid var(--border);background:rgba(32,44,51,.45);font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px
    }
    .btn{
      margin-top:10px;height:36px;padding:0 12px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--text);cursor:pointer
    }
    .btn.primary{border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.16)}
    .btn.dangerBtn{border-color:rgba(225,29,72,.45);background:rgba(225,29,72,.14);color:#fecdd3}
    .invoiceTableWrap{margin-top:12px;border:1px solid var(--border);border-radius:12px;overflow:auto}
    .invoiceHead,.invoiceRow{display:grid;grid-template-columns:1.1fr .9fr 1.7fr .7fr;gap:10px;align-items:center;padding:10px 12px}
    .invoiceHead{font-size:11px;text-transform:uppercase;letter-spacing:.3px;font-weight:700;color:var(--muted);border-bottom:1px solid var(--border);background:rgba(42,57,66,.25)}
    .invoiceRow{border-bottom:1px solid rgba(42,57,66,.35)}
    .invoiceRow:last-child{border-bottom:0}
    .invId{font-weight:800;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px}
    .invUrl{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .invUrl a{color:#7dd3fc;text-decoration:none}
    .invUrl a:hover{text-decoration:underline}
    .pager{margin-top:10px;display:flex;justify-content:center;gap:10px;align-items:center;padding:8px 10px}
    .pagerBtn{
      border:1px solid var(--border);
      background:transparent;
      color:var(--text);
      border-radius:10px;
      height:34px;
      padding:0 12px;
      cursor:pointer;
      font-size:12px;
    }
    .pagerBtn:hover{border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.10)}
    .pagerBtn:disabled{opacity:.45;cursor:not-allowed}
    .invoiceBtn{display:inline-flex;align-items:center;justify-content:center;text-decoration:none}
    .pageInfo{margin:0;min-width:90px;text-align:center}
    .emptyRow{padding:10px 12px}
    .toast{
      position:fixed;right:20px;bottom:20px;z-index:40;
      padding:8px 12px;border-radius:10px;border:1px solid rgba(0,168,132,.45);background:rgba(0,168,132,.18);font-size:12px
    }
    .accountActions{margin-top:12px}
    :host-context(body.light) .hero,
    :host-context(body.light) .card{
      background:rgba(255,255,255,.92);
      border-color:rgba(15,23,42,.10);
    }
    :host-context(body.light) .menuItem:hover{background:rgba(15,23,42,.06);color:#0f172a}
    :host-context(body.light) .menuItem.active{background:rgba(2,132,199,.12);border-color:rgba(2,132,199,.35);color:#0f172a}
    :host-context(body.light) .form input{background:#fff;color:#0f172a}
    :host-context(body.light) .metaValue{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.12)}
    :host-context(body.light) .invoiceHead{background:rgba(15,23,42,.04)}
    :host-context(body.light) .invoiceRow{border-bottom-color:rgba(15,23,42,.08)}
    :host-context(body.light) .invUrl a{color:#0369a1}
    :host-context(body.light) .h1,:host-context(body.light) .t{color:#0f172a}
    :host-context(body.light) .h2,:host-context(body.light) .m,:host-context(body.light) .metaLabel,:host-context(body.light) .form label{color:#475569}
    @media (max-width: 900px){
      .layout{grid-template-columns:1fr}
      .metaGrid{grid-template-columns:1fr}
    }
  `]
})
export class ProfilePageComponent implements OnInit {
  activeTab = signal<'config' | 'invoice'>('config');
  toast = signal<string>('');
  invoicePage = signal<number>(1);
  readonly invoicePageSize = 6;
  invoiceLoading = false;
  invoiceError = '';

  invoices: InvoiceApiItem[] = [];

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly invoicesApi: InvoicesApiService
  ) {}

  get orgId(): string {
    return readStoredOrgId();
  }

  get orgName(): string {
    return readStoredOrgName();
  }

  get chatbotNumber(): string {
    return readStoredChatbotNumber();
  }

  get wabaId(): string {
    return readStoredWabaId();
  }

  get phoneNumberId(): string {
    return readStoredPhoneNumberId();
  }

  get businessId(): string {
    return readStoredBusinessId();
  }

  async ngOnInit(): Promise<void> {
    await this.loadInvoices();
  }

  logout(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }

  totalInvoicePages(): number {
    return Math.max(1, Math.ceil(this.invoices.length / this.invoicePageSize));
  }

  pagedInvoices(): Array<{ id: string; createdAt: string; url: string }> {
    const safePage = Math.min(this.invoicePage(), this.totalInvoicePages());
    const start = (safePage - 1) * this.invoicePageSize;
    return this.invoices.slice(start, start + this.invoicePageSize);
  }

  goToPrevInvoicePage(): void {
    this.invoicePage.update(page => Math.max(1, page - 1));
  }

  goToNextInvoicePage(): void {
    this.invoicePage.update(page => Math.min(this.totalInvoicePages(), page + 1));
  }

  private async loadInvoices(): Promise<void> {
    this.invoiceLoading = true;
    this.invoiceError = '';
    try {
      this.invoices = await this.invoicesApi.listInvoices();
      this.invoicePage.set(1);
    } catch (error) {
      console.error('Failed to load invoices', error);
      this.invoiceError = 'Unable to load invoices from backend.';
    } finally {
      this.invoiceLoading = false;
    }
  }
}
